import { create } from 'zustand';
import {
    User,
    CreateUserDto,
    UpdateUserDto,
    ChangePasswordDto,
    UpdatePasswordDto,
    GetPaginationUsersDto
} from '@/types/user';
import { userService } from '@/services/userService';
import { showToast } from '@/lib/toast';

interface UserState {
    // Data state
    users: User[];
    currentUser: User | null;
    selectedUser: User | null;

    // Pagination state
    currentPage: number;
    totalPages: number;
    totalItems: number;

    // Loading states
    isLoading: boolean;
    isLoadingDetail: boolean;
    isCreating: boolean;
    isUpdating: boolean;
    isDeleting: boolean;
    isChangingPassword: boolean;

    // Error states
    error: string | null;
    detailError: string | null;

    // Actions
    fetchUsers: (params?: GetPaginationUsersDto) => Promise<void>;
    fetchAllUsers: () => Promise<void>;
    fetchUserById: (id: string) => Promise<void>;
    createUser: (data: CreateUserDto) => Promise<boolean>;
    updateUser: (id: string, data: UpdateUserDto) => Promise<boolean>;
    deleteUser: (id: string) => Promise<boolean>;
    changePassword: (data: ChangePasswordDto) => Promise<boolean>;
    updatePassword: (id: string, data: UpdatePasswordDto) => Promise<boolean>;
    clearSelectedUser: () => void;
    clearError: () => void;
    setCurrentPage: (page: number) => void;
}

export const useUserStore = create<UserState>()((set, get) => ({
    // Initial state
    users: [],
    currentUser: null,
    selectedUser: null,

    currentPage: 1,
    totalPages: 0,
    totalItems: 0,

    isLoading: false,
    isLoadingDetail: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isChangingPassword: false,

    error: null,
    detailError: null,

    // Actions
    fetchUsers: async (params) => {
        set({ isLoading: true, error: null });
        try {
            const response: any = await userService.getPaginationUsers(params || {
                pageIndex: get().currentPage,
                pageSize: 10
            });
            console.log("response: ", response)
            if (response.ok && response.data) {
                set({
                    users: response?.data?.data.users || [],
                    currentPage: params?.pageIndex || get().currentPage,
                    totalPages: response?.data?.data.totalPages,
                    totalItems: response?.data?.data.total,
                    isLoading: false
                });
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Lỗi khi tải danh sách người dùng';
            set({
                error: errorMessage,
                isLoading: false
            });
            showToast.error("Lỗi", errorMessage);
        }
    },

    fetchAllUsers: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await userService.getAllUsers();
            if (response.ok && response.data) {
                set({
                    users: Array.isArray(response.data) ? response.data : [],
                    isLoading: false
                });
            }
        } catch (error) {
            set({
                error: error instanceof Error ? error.message : 'Lỗi khi tải tất cả người dùng',
                isLoading: false
            });
        }
    },

    fetchUserById: async (id) => {
        set({ isLoadingDetail: true, detailError: null });
        try {
            const response = await userService.getUserById(id);
            console.log("response: ", response)
            if (response.ok && response.data) {
                set({
                    selectedUser: response.data.data || null,
                    isLoadingDetail: false
                });
            }
        } catch (error) {
            set({
                detailError: error instanceof Error ? error.message : 'Lỗi khi tải thông tin người dùng',
                isLoadingDetail: false
            });
        }
    },

    createUser: async (data) => {
        set({ isCreating: true, error: null });
        try {
            const response = await userService.createUser(data);
            set({ isCreating: false });

            if (response.ok && response.data) {
                // Refresh user list after creating
                get().fetchUsers();
                return true;
            }
            return false;
        } catch (error) {
            set({
                error: error instanceof Error ? error.message : 'Lỗi khi tạo người dùng mới',
                isCreating: false
            });
            return false;
        }
    },

    updateUser: async (id, data) => {
        set({ isUpdating: true, error: null });
        try {
            const response = await userService.updateUser(id, data);
            set({ isUpdating: false });

            if (response.ok && response.data) {
                // Update the selected user if it's the one being edited
                if (get().selectedUser?._id === id) {
                    set({ selectedUser: response.data.user || null });
                }

                // Refresh user list after updating
                get().fetchUsers();
                return true;
            }
            return false;
        } catch (error) {
            set({
                error: error instanceof Error ? error.message : 'Lỗi khi cập nhật người dùng',
                isUpdating: false
            });
            return false;
        }
    },

    deleteUser: async (id) => {
        set({ isDeleting: true, error: null });
        try {
            const response = await userService.deleteUser(id);
            set({ isDeleting: false });

            if (response.ok && response.data) {
                // Clear selected user if it's the one being deleted
                if (get().selectedUser?._id === id) {
                    set({ selectedUser: null });
                }

                // Refresh user list after deleting
                get().fetchUsers();
                return true;
            }
            return false;
        } catch (error) {
            set({
                error: error instanceof Error ? error.message : 'Lỗi khi xóa người dùng',
                isDeleting: false
            });
            return false;
        }
    },

    changePassword: async (data) => {
        set({ isChangingPassword: true, error: null });
        try {
            const response = await userService.changePassword(data);
            set({ isChangingPassword: false });

            if (response.ok && response.data) {
                return true;
            }
            return false;
        } catch (error) {
            set({
                error: error instanceof Error ? error.message : 'Lỗi khi thay đổi mật khẩu',
                isChangingPassword: false
            });
            return false;
        }
    },

    updatePassword: async (id, data) => {
        set({ isChangingPassword: true, error: null });
        try {
            const response = await userService.updatePassword(id, data);
            set({ isChangingPassword: false });

            if (response.ok && response.data) {
                return true;
            }
            return false;
        } catch (error) {
            set({
                error: error instanceof Error ? error.message : 'Lỗi khi cập nhật mật khẩu',
                isChangingPassword: false
            });
            return false;
        }
    },

    clearSelectedUser: () => set({ selectedUser: null, detailError: null }),
    clearError: () => set({ error: null, detailError: null }),
    setCurrentPage: (page) => set({ currentPage: page })
}));
